import { useEffect, useState } from 'react';
import { studioApi } from '@/lib/studioApi';
import { loadStudioImage } from '@/hooks/useStudioImage';
import { cloneArt, rasterise } from './captureArt';

const jobs = new Map();
export const loadDepth = assetId => {
  if (!jobs.has(assetId)) jobs.set(assetId, studioApi(`/uploads/${assetId}/depth`, { method: 'POST' }).then(d => loadStudioImage(d.id)).catch(e => { jobs.delete(assetId); throw e; }));
  return jobs.get(assetId);
};

export const maxReliefMm = product => Math.max(4, Math.min(40, (product.depth_mm || 50) - 12));
export const halfDepth = product => (product.depth_mm || 50) / 2;
export const fromFront = (o, product) => halfDepth(product) - zOf(o, product);
export const zLimit = product => Math.max(2, halfDepth(product) - 5);
export const defaultZ = o => o.type === 'image' ? 0 : 8;
export const zOf = (o, product) => Math.max(-zLimit(product), Math.min(zLimit(product), o.glassZ ?? defaultZ(o)));

// AI depth maps (bright = near) for every picture on a crystal article, computed once per picture.
export const useDepthMaps = (objects, enabled) => {
  const ids = enabled ? [...new Set(objects.filter(o => o.type === 'image' && o.assetId && !o.placeholder).map(o => o.assetId))] : [];
  const key = ids.join('|'), [urls, setUrls] = useState({}), [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    ids.forEach(id => loadDepth(id).then(url => { if (alive) { setUrls(u => ({ ...u, [id]: url })); setError(null); } }).catch(e => { if (alive) setError(e.message); }));
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { urls, pending: ids.filter(id => !urls[id]).length, error, key: ids.map(id => urls[id] ? id : '').join('|') };
};

const NS = 'http://www.w3.org/2000/svg';
// Two aligned rasters of the engraving area: brightness (photo positive, text white) and z (grey 128 = centre plane).
export const captureCrystal = (product, depthFor, objectFor) => async (svg, area) => {
  const px = Math.min(420, Math.round((product.area_width_mm || 50) * 7)), half = halfDepth(product);
  const a = cloneArt(svg, area, px), d = cloneArt(svg, area, px);
  if (!a || !d) return null;
  a.out.querySelectorAll('image').forEach(img => img.removeAttribute('filter'));
  const defs = d.out.querySelector('defs') || d.out.insertBefore(document.createElementNS(NS, 'defs'), d.out.firstChild);
  d.out.querySelectorAll('[data-object-id]').forEach((g, n) => {
    const o = objectFor(g.getAttribute('data-object-id')); if (!o) return;
    const z = zOf(o, product), v = .5 + z / (2 * half), grey = Math.round(Math.max(0, Math.min(1, v)) * 255), hex = `rgb(${grey},${grey},${grey})`;
    [g, ...g.querySelectorAll('[style]')].forEach(n2 => { if (n2.style) n2.style.color = hex; });
    g.querySelectorAll('image').forEach(img => {
      const url = o.type === 'image' ? depthFor(o) : null, slope = url ? maxReliefMm(product) * (o.relief ?? 60) / 100 / (2 * half) : 0;
      const f = document.createElementNS(NS, 'filter'), id = `cz${n}`; f.setAttribute('id', id); f.setAttribute('color-interpolation-filters', 'sRGB');
      const ct = document.createElementNS(NS, 'feComponentTransfer');
      ['R', 'G', 'B'].forEach(c => { const fn = document.createElementNS(NS, `feFunc${c}`); fn.setAttribute('type', 'linear'); fn.setAttribute('slope', slope); fn.setAttribute('intercept', v - slope / 2); ct.append(fn); });
      f.append(ct); defs.append(f);
      if (url) img.setAttribute('href', url);
      img.setAttribute('filter', `url(#${id})`);
    });
  });
  const [ci, cd] = await Promise.all([rasterise(a, '#000000'), rasterise(d)]);
  if (!ci || !cd) return null;
  return { intensity: ci.getContext('2d').getImageData(0, 0, ci.width, ci.height).data, depth: cd.getContext('2d').getImageData(0, 0, cd.width, cd.height).data, w: ci.width, h: ci.height };
};

// Rebuilds the production cloud from the server grid with the same rules as crystal_export.grid_points.
export const decodeCrystal = buffer => {
  const v = new DataView(buffer), w = v.getInt32(0, true), h = v.getInt32(4, true), T = v.getInt32(8, true), L = v.getInt32(12, true);
  const spacing = v.getFloat32(16, true), gap = v.getFloat32(20, true), scale = v.getFloat32(24, true), n = T * T;
  const ranks = new Uint16Array(buffer, 32, n), lo = 32 + n * 2, level = new Uint8Array(buffer, lo, w * h), zq = new Int16Array(buffer, lo + w * h + ((w * h) % 2), w * h);
  const keep = (r, c, l, value) => Math.min(1, Math.max(0, value - l)) > (ranks[((r + 23 * l) % T) * T + ((c + 41 * l) % T)] + .5) / n;
  let count = 0;
  for (let l = 0; l < L; l++) for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) { const lv = level[r * w + c]; if (lv && keep(r, c, l, lv / 255 * scale * L)) count++; }
  const out = new Float32Array(count * 3), hw = w * spacing / 2, hh = h * spacing / 2;
  let q = 0;
  for (let l = 0; l < L; l++) {
    const lz = (l - (L - 1) / 2) * gap, salt = Math.imul(l, 83492791);
    for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) {
      const i = r * w + c, lv = level[i];
      if (!lv || !keep(r, c, l, lv / 255 * scale * L)) continue;
      const hsh = (Math.imul(r, 73856093) ^ Math.imul(c, 19349663) ^ salt) >>> 0;
      out[q++] = (c + .5 + ((hsh & 0x7ff) / 2048 - .5) * .9) * spacing - hw;
      out[q++] = hh - (r + .5 + (((hsh >>> 11) & 0x7ff) / 2048 - .5) * .9) * spacing;
      out[q++] = zq[i] / 100 + lz + ((hsh >>> 22) / 1024 - .5) * .05;
    }
  }
  return { points: out, spacing };
};

// Exact preview: the server's production grid, refreshed after a short pause in editing.
export const useExactCrystal = (design, ready) => {
  const [state, setState] = useState({ key: '', cloud: null, busy: false });
  const key = ready && design ? JSON.stringify(design) : '';
  useEffect(() => {
    if (!key) return undefined;
    let alive = true; setState(s => ({ ...s, busy: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await studioApi('/crystal/preview', { method: 'POST', body: design, raw: true });
        const cloud = decodeCrystal(await res.arrayBuffer());
        if (alive) setState({ key, cloud, busy: false });
      } catch { if (alive) setState(s => ({ ...s, busy: false })); }
    }, 1400);
    return () => { alive = false; clearTimeout(timer); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const cloud = state.key === key ? state.cloud : null;
  return { points: cloud?.points || null, spacing: cloud?.spacing, busy: state.busy };
};

// Point cloud in mm around the block centre (same rules as the production file, lower density for the browser).
export const crystalPoints = (cap, product, limit = 600000) => {
  const { intensity: I, depth: D, w, h } = cap, aw = product.area_width_mm, ah = product.area_height_mm, mx = aw / w, my = ah / h;
  const half = halfDepth(product), out = [];
  let seed = 7; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = (y * w + x) * 4, lum = (.2126 * I[i] + .7152 * I[i + 1] + .0722 * I[i + 2]) / 255, level = Math.min(1, Math.max(0, (lum - .06) / .94));
    if (!level) continue;
    const want = level ** 1.2 * 1.6, n = Math.floor(want) + (rnd() < want % 1 ? 1 : 0), z = D[i + 3] > 20 ? (D[i] / 255 * 2 - 1) * half : 8;
    for (let k = 0; k < n; k++) out.push((x + rnd()) * mx - aw / 2, ah / 2 - (y + rnd()) * my, z + (rnd() - .5) * .25);
  }
  const count = out.length / 3, stride = Math.max(1, Math.ceil(count / limit));
  if (stride === 1) return new Float32Array(out);
  const thin = new Float32Array(Math.floor(count / stride) * 3);
  for (let p = 0, q = 0; q < thin.length; p += stride * 3, q += 3) { thin[q] = out[p]; thin[q + 1] = out[p + 1]; thin[q + 2] = out[p + 2]; }
  return thin;
};
